import {
  Button,
  Card,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Modal,
  Select,
  Space,
} from 'antd';
import Table, { ColumnsType } from 'antd/es/table';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { getProductActivityList, PmsProductActivity } from 'apis/pms';
import { getUmsAdminList } from 'apis/sys';
import usePagination from 'commons/hooks/usePagination';
import LOCALS from 'commons/locals';
import showTotal from 'components/show-total';
import JSONViewer from 'components/simple-json-viewer';
import dayjs, { Dayjs } from 'dayjs';
import i18n from 'i18n';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState,
} from 'nuqs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { Link } from 'react-router-dom';

const hasActivityDetail = (actDetail?: string) => !!actDetail?.trim();

const parseActivityDetail = (actDetail?: string) => {
  if (!actDetail?.trim()) return null;

  try {
    return JSON.parse(actDetail);
  } catch {
    return null;
  }
};

const useQueryParams = () => {
  const [pageNum, setPageNum] = useQueryState(
    'pageNum',
    parseAsInteger.withDefault(1),
  );
  const [pageSize, setPageSize] = useQueryState(
    'pageSize',
    parseAsInteger.withDefault(10),
  );
  const [productIds, setProductIds] = useQueryState(
    'productIds',
    parseAsArrayOf(parseAsInteger).withDefault([]),
  );
  const [usernames, setUsernames] = useQueryState(
    'usernames',
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  const [emails, setEmails] = useQueryState(
    'emails',
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  const [action, setAction] = useQueryState(
    'action',
    parseAsString.withDefault(''),
  );
  const [startDate, setStartDate] = useQueryState(
    'startDate',
    parseAsString.withDefault(''),
  );
  const [endDate, setEndDate] = useQueryState(
    'endDate',
    parseAsString.withDefault(''),
  );

  return {
    pageNum,
    pageSize,
    productIds,
    usernames,
    emails,
    action,
    startDate,
    endDate,
    setPageNum,
    setPageSize,
    setProductIds,
    setUsernames,
    setEmails,
    setAction,
    setStartDate,
    setEndDate,
  };
};

const ProductActivityPage = () => {
  const [form] = Form.useForm();
  const [selectedActivity, setSelectedActivity] =
    useState<PmsProductActivity | null>(null);

  const {
    pageNum,
    pageSize,
    productIds,
    usernames,
    emails,
    action,
    startDate,
    endDate,
    setPageNum,
    setPageSize,
    setProductIds,
    setUsernames,
    setEmails,
    setAction,
    setStartDate,
    setEndDate,
  } = useQueryParams();

  const { loading, setLoading, total, setTotal, dataSource, setDataSource } =
    usePagination<PmsProductActivity>();

  const [adminOptions, setAdminOptions] = useState<
    { label: string; value: string }[]
  >([]);

  useEffect(() => {
    getUmsAdminList()
      .then((list) => {
        setAdminOptions(
          list.map(({ username, nickName }) => ({
            value: username,
            label: nickName ? `${nickName} (${username})` : username,
          })),
        );
      })
      .catch(() => {
        setAdminOptions([]);
      });
  }, []);

  const getDataSource = useCallback(async () => {
    try {
      setLoading(true);
      const { list, total: nextTotal } = await getProductActivityList({
        pageNum,
        pageSize,
        productIds: productIds.length ? productIds : undefined,
        usernames: usernames.length ? usernames : undefined,
        emails: emails.length ? emails : undefined,
        action: action || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      setDataSource(list);
      setTotal(nextTotal);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [
    action,
    endDate,
    pageNum,
    pageSize,
    productIds,
    setDataSource,
    setLoading,
    setTotal,
    startDate,
    usernames,
    emails,
  ]);

  const dateRange = useMemo<[Dayjs | null, Dayjs | null] | null>(() => {
    if (!startDate && !endDate) return null;
    return [
      startDate ? dayjs(startDate) : null,
      endDate ? dayjs(endDate) : null,
    ];
  }, [endDate, startDate]);

  useEffect(() => {
    form.setFieldsValue({
      productIds: productIds.length ? productIds : undefined,
      usernames: usernames.length ? usernames : undefined,
      emails: emails.length ? emails : undefined,
      action,
      dateRange,
    });
  }, [action, dateRange, emails, form, productIds, usernames]);

  useEffect(() => {
    getDataSource();
  }, [getDataSource]);

  const activityListWithDetail = useMemo(
    () => dataSource.filter((item) => hasActivityDetail(item.actDetail)),
    [dataSource],
  );

  const selectedIndex = useMemo(() => {
    if (!selectedActivity) return -1;
    return activityListWithDetail.findIndex(
      (item) => item.actId === selectedActivity.actId,
    );
  }, [activityListWithDetail, selectedActivity]);

  const canGoPrev = selectedIndex > 0;
  const canGoNext =
    selectedIndex >= 0 && selectedIndex < activityListWithDetail.length - 1;

  const selectedDetail = useMemo(
    () => parseActivityDetail(selectedActivity?.actDetail),
    [selectedActivity],
  );

  const columns: ColumnsType<PmsProductActivity> = useMemo(
    () => [
      {
        title: <Trans i18nKey={LOCALS.product_id} />,
        dataIndex: 'actProductId',
        key: 'actProductId',
        width: 100,
        render: (id: number) =>
          id ? <Link to={`/pms/product-view/${id}`}>{id}</Link> : '-',
      },
      {
        title: <Trans i18nKey={LOCALS.operator} />,
        dataIndex: 'actUsername',
        key: 'actUsername',
        render: (value?: string) => value || '-',
      },
      {
        title: <Trans i18nKey={LOCALS.operation_time} />,
        dataIndex: 'actCreateAt',
        key: 'actCreateAt',
        render: (actCreateAt?: string) =>
          actCreateAt ? dayjs(actCreateAt).format('YYYY-MM-DD HH:mm:ss') : '-',
      },
      {
        title: <Trans i18nKey={LOCALS.option} />,
        dataIndex: 'actAction',
        key: 'actAction',
        render: (actAction: string, record: PmsProductActivity) => {
          const detailObj = parseActivityDetail(record.actDetail);

          if (!detailObj?.price) {
            return <div>{actAction || '-'}</div>;
          }

          return (
            <div>
              <span>{actAction}</span>
              <span className="text-red-700">
                {!!detailObj.price?.from && (
                  <span className="ml-4">
                    price from {detailObj.price.from.toLocaleString()}
                  </span>
                )}
                {!!detailObj.price?.to && (
                  <span className="ml-4">
                    price to {detailObj.price.to.toLocaleString()}
                  </span>
                )}
              </span>
            </div>
          );
        },
      },
      {
        title: <Trans i18nKey={LOCALS.details} />,
        dataIndex: 'actDetail',
        key: 'actDetail',
        width: 120,
        render: (actDetail: string, record: PmsProductActivity) => {
          if (!hasActivityDetail(actDetail)) {
            return '-';
          }

          return (
            <Button onClick={() => setSelectedActivity(record)}>
              {i18n.t(LOCALS.details)}
            </Button>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <Card bodyStyle={{ padding: '16px' }}>
        <Form
          form={form}
          layout="inline"
          onFinish={(values) => {
            setProductIds(
              (values.productIds || [])
                .map((item: string | number) => Number(item))
                .filter((id: number) => Number.isInteger(id) && id > 0),
            );
            setUsernames(
              (values.usernames || [])
                .map((item: string) => item.trim())
                .filter(Boolean),
            );
            setEmails(
              (values.emails || [])
                .map((item: string) => item.trim())
                .filter(Boolean),
            );
            setAction(values.action?.trim() || '');
            setStartDate(
              values.dateRange?.[0]
                ? values.dateRange[0]
                    .startOf('day')
                    .format('YYYY-MM-DD HH:mm:ss')
                : '',
            );
            setEndDate(
              values.dateRange?.[1]
                ? values.dateRange[1].endOf('day').format('YYYY-MM-DD HH:mm:ss')
                : '',
            );
            setPageNum(1);
          }}
        >
          <Form.Item
            label={<Trans i18nKey={LOCALS.product_id} />}
            name="productIds"
          >
            <Select
              mode="tags"
              allowClear
              tokenSeparators={[',', ' ', '\n']}
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              style={{ minWidth: 220 }}
            />
          </Form.Item>
          <Form.Item
            label={<Trans i18nKey={LOCALS.product_activity_admin} />}
            name="usernames"
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              options={adminOptions}
              placeholder={i18n.t(LOCALS.please_select) || ''}
              style={{ minWidth: 220 }}
            />
          </Form.Item>
          <Form.Item
            label={<Trans i18nKey={LOCALS.product_activity_member_email} />}
            name="emails"
          >
            <Select
              mode="tags"
              allowClear
              tokenSeparators={[',']}
              placeholder={i18n.t(LOCALS.pwr_please_enter_email) || ''}
              style={{ minWidth: 220 }}
            />
          </Form.Item>
          <Form.Item label={<Trans i18nKey={LOCALS.option} />} name="action">
            <Input allowClear placeholder={i18n.t(LOCALS.please_enter) || ''} />
          </Form.Item>
          <Form.Item
            label={<Trans i18nKey={LOCALS.operation_time} />}
            name="dateRange"
          >
            <DatePicker.RangePicker />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                <Trans i18nKey={LOCALS.search} />
              </Button>
              <Button
                htmlType="button"
                onClick={() => {
                  form.resetFields();
                  setProductIds([]);
                  setUsernames([]);
                  setEmails([]);
                  setAction('');
                  setStartDate('');
                  setEndDate('');
                  setPageNum(1);
                  setPageSize(10);
                }}
              >
                <Trans i18nKey={LOCALS.reset} />
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card bodyStyle={{ padding: '16px' }}>
        <Table
          bordered
          pagination={{
            total,
            pageSize,
            current: pageNum,
            showTotal,
            onChange: (page, nextPageSize) => {
              setPageNum(page);
              setPageSize(nextPageSize);
            },
          }}
          loading={loading}
          rowKey="actId"
          dataSource={dataSource}
          columns={columns}
          scroll={{ x: 'max-content' }}
          tableLayout="auto"
        />
      </Card>

      <Modal
        title={i18n.t(LOCALS.details)}
        open={!!selectedActivity}
        onCancel={() => setSelectedActivity(null)}
        footer={null}
        width="1200px"
      >
        {selectedActivity && (
          <>
            {activityListWithDetail.length > 1 && (
              <div className="mb-4 flex items-center justify-center gap-3">
                <Button
                  type="text"
                  icon={<LeftOutlined />}
                  disabled={!canGoPrev}
                  onClick={() => {
                    if (canGoPrev) {
                      setSelectedActivity(
                        activityListWithDetail[selectedIndex - 1],
                      );
                    }
                  }}
                />
                <span className="text-sm text-gray-500">
                  {selectedIndex + 1} / {activityListWithDetail.length}
                </span>
                <Button
                  type="text"
                  icon={<RightOutlined />}
                  disabled={!canGoNext}
                  onClick={() => {
                    if (canGoNext) {
                      setSelectedActivity(
                        activityListWithDetail[selectedIndex + 1],
                      );
                    }
                  }}
                />
              </div>
            )}
            <Descriptions
              bordered
              size="small"
              column={1}
              className="mb-4"
              items={[
                {
                  key: 'actProductId',
                  label: i18n.t(LOCALS.product_id),
                  children: selectedActivity.actProductId ? (
                    <Link
                      to={`/pms/product-view/${selectedActivity.actProductId}`}
                    >
                      {selectedActivity.actProductId}
                    </Link>
                  ) : (
                    '-'
                  ),
                },
                {
                  key: 'actUsername',
                  label: i18n.t(LOCALS.operator),
                  children: selectedActivity.actUsername || '-',
                },
                {
                  key: 'actCreateAt',
                  label: i18n.t(LOCALS.operation_time),
                  children: selectedActivity.actCreateAt
                    ? dayjs(selectedActivity.actCreateAt).format(
                        'YYYY-MM-DD HH:mm:ss',
                      )
                    : '-',
                },
                {
                  key: 'actAction',
                  label: i18n.t(LOCALS.option),
                  children: selectedActivity.actAction || '-',
                },
              ]}
            />
            {selectedDetail && typeof selectedDetail === 'object' ? (
              <JSONViewer data={selectedDetail} />
            ) : (
              <pre className="whitespace-pre-wrap break-all rounded bg-gray-100 p-4">
                {selectedActivity.actDetail}
              </pre>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default ProductActivityPage;
